import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, MessageCircle } from "lucide-react";
import TopNav from "@/components/TopNav";
import CinematicNav from "@/components/CinematicNav";
import { useAriaVoice } from "@/hooks/useAriaVoice";
import { AriaLeadForm } from "@/components/AriaFloatingWidget";
import AriaParticleFace from "@/components/AriaParticleFace";

gsap.registerPlugin(ScrollTrigger);

/**
 * TotalKlick · Home Cinematica 2026
 *
 * NON è un sito aziendale. NON è una landing SaaS. NON è un catalogo prodotti.
 * È un viaggio continuo che porta dall'incontro con Aria fino all'ingresso
 * nell'Ecosistema. Aria è il sistema nervoso. I moduli sono applicazioni.
 *
 * Unico particle field Three.js a sfondo (fixed) con phase change scroll-driven.
 * 13 momenti narrativi, niente etichette tecniche, ritmo serrato (~70vh per atto).
 */

// ============================================================
// PARTICLE FIELD — unico sfondo per tutto il viaggio
// ============================================================
const PARTICLE_COUNT = 4500;

// Ogni "phase" = configurazione target del particle field
function generatePhasePositions(phase, count) {
    const positions = new Float32Array(count * 3);
    const r = (a, b) => a + Math.random() * (b - a);

    switch (phase) {
        case 0: // origine — punto centrale
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = r(-0.3, 0.3);
                positions[i * 3 + 1] = r(-0.3, 0.3);
                positions[i * 3 + 2] = r(-0.3, 0.3);
            }
            break;
        case 1: // presenza — sfera diffusa
            for (let i = 0; i < count; i++) {
                const rad = r(10, 22);
                const th = r(0, Math.PI * 2);
                const ph = Math.acos(r(-1, 1));
                positions[i * 3 + 0] = rad * Math.sin(ph) * Math.cos(th);
                positions[i * 3 + 1] = rad * Math.sin(ph) * Math.sin(th);
                positions[i * 3 + 2] = rad * Math.cos(ph);
            }
            break;
        case 2: // ovunque — box ampio
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = r(-42, 42);
                positions[i * 3 + 1] = r(-24, 24);
                positions[i * 3 + 2] = r(-25, 5);
            }
            break;
        case 3: // problema — cluster isolati
            {
                const cn = 12;
                const cs = [];
                for (let c = 0; c < cn; c++) cs.push([r(-32, 32), r(-18, 18), r(-20, 0)]);
                for (let i = 0; i < count; i++) {
                    const c = cs[i % cn];
                    positions[i * 3 + 0] = c[0] + r(-2, 2);
                    positions[i * 3 + 1] = c[1] + r(-2, 2);
                    positions[i * 3 + 2] = c[2] + r(-2, 2);
                }
            }
            break;
        case 4: // nascita — cluster + linee
            {
                const cn = 14;
                const cs = [];
                for (let c = 0; c < cn; c++) cs.push([r(-26, 26), r(-16, 16), r(-18, 2)]);
                for (let i = 0; i < count; i++) {
                    const c = cs[i % cn];
                    positions[i * 3 + 0] = c[0] + r(-1.5, 1.5);
                    positions[i * 3 + 1] = c[1] + r(-1.5, 1.5);
                    positions[i * 3 + 2] = c[2] + r(-1.5, 1.5);
                }
            }
            break;
        case 5: // host-first — due cluster + ponte
            for (let i = 0; i < count; i++) {
                const t = i / count;
                if (t < 0.45) {
                    positions[i * 3 + 0] = -14 + r(-3, 3);
                    positions[i * 3 + 1] = r(-7, 7);
                    positions[i * 3 + 2] = r(-10, 0);
                } else if (t < 0.55) {
                    positions[i * 3 + 0] = r(-12, 12);
                    positions[i * 3 + 1] = r(-1.5, 1.5);
                    positions[i * 3 + 2] = r(-8, 0);
                } else {
                    positions[i * 3 + 0] = 16 + r(-4, 4);
                    positions[i * 3 + 1] = r(-10, 10);
                    positions[i * 3 + 2] = r(-12, 0);
                }
            }
            break;
        case 6: // ecosistema — sfera centrale + 7 satelliti
            {
                const moduleAngles = [-1.4, -0.95, -0.5, -0.05, 0.4, 0.85, 1.3];
                for (let i = 0; i < count; i++) {
                    const t = i / count;
                    if (t < 0.55) {
                        // Aria centrale
                        const rad = r(1.5, 7);
                        const th = r(0, Math.PI * 2);
                        const ph = Math.acos(r(-1, 1));
                        positions[i * 3 + 0] = rad * Math.sin(ph) * Math.cos(th);
                        positions[i * 3 + 1] = rad * Math.sin(ph) * Math.sin(th) * 0.7;
                        positions[i * 3 + 2] = rad * Math.cos(ph);
                    } else {
                        // satelliti orbitanti
                        const a = moduleAngles[i % moduleAngles.length];
                        const orbit = 15 + r(-1, 1);
                        const jitter = r(-0.8, 0.8);
                        positions[i * 3 + 0] = Math.cos(a) * orbit + jitter;
                        positions[i * 3 + 1] = Math.sin(a) * orbit * 0.45 + jitter;
                        positions[i * 3 + 2] = r(-4, 1);
                    }
                }
            }
            break;
        case 7: // rete cresce — costellazione densa
            for (let i = 0; i < count; i++) {
                const rad = r(6, 24);
                const th = r(0, Math.PI * 2);
                positions[i * 3 + 0] = rad * Math.cos(th);
                positions[i * 3 + 1] = r(-12, 12);
                positions[i * 3 + 2] = rad * Math.sin(th) - 6;
            }
            break;
        case 8: // volto — mai completo
            for (let i = 0; i < count; i++) {
                const t = Math.random();
                if (t < 0.6) {
                    const a = r(0, Math.PI * 2);
                    const rad = r(7, 9);
                    positions[i * 3 + 0] = Math.cos(a) * rad * 0.78 + r(-0.6, 0.6);
                    positions[i * 3 + 1] = Math.sin(a) * rad * 1.05 + r(-0.6, 0.6);
                    positions[i * 3 + 2] = r(-6, 2);
                } else if (t < 0.74) {
                    positions[i * 3 + 0] = -2.6 + r(-0.8, 0.8);
                    positions[i * 3 + 1] = 1.6 + r(-0.4, 0.4);
                    positions[i * 3 + 2] = r(-5, 1);
                } else if (t < 0.88) {
                    positions[i * 3 + 0] = 2.6 + r(-0.8, 0.8);
                    positions[i * 3 + 1] = 1.6 + r(-0.4, 0.4);
                    positions[i * 3 + 2] = r(-5, 1);
                } else {
                    positions[i * 3 + 0] = r(-12, 12);
                    positions[i * 3 + 1] = r(-12, 12);
                    positions[i * 3 + 2] = r(-6, 2);
                }
            }
            break;
        case 9: // flusso verticale
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = r(-3, 3);
                positions[i * 3 + 1] = r(-15, 15);
                positions[i * 3 + 2] = r(-8, 2);
            }
            break;
        case 10: // installazione — 3 nuclei
            for (let i = 0; i < count; i++) {
                const slot = i % 3;
                const cx = (slot - 1) * 14;
                positions[i * 3 + 0] = cx + r(-3, 3);
                positions[i * 3 + 1] = r(-4, 4);
                positions[i * 3 + 2] = r(-8, 0);
            }
            break;
        case 11: // numeri — disco
            for (let i = 0; i < count; i++) {
                const a = r(0, Math.PI * 2);
                const rad = r(0, 18);
                positions[i * 3 + 0] = Math.cos(a) * rad;
                positions[i * 3 + 1] = r(-0.6, 0.6) + Math.sin(i * 0.1) * 1.5;
                positions[i * 3 + 2] = Math.sin(a) * rad - 4;
            }
            break;
        case 12: // chiusura — quasi statico
            for (let i = 0; i < count; i++) {
                const t = i / count;
                const rad = r(4, 16) * (1 - t * 0.3);
                const th = r(0, Math.PI * 2);
                positions[i * 3 + 0] = rad * Math.cos(th);
                positions[i * 3 + 1] = rad * Math.sin(th) * 0.55;
                positions[i * 3 + 2] = r(-12, 0);
            }
            break;
        default:
            for (let i = 0; i < count; i++) {
                positions[i * 3 + 0] = r(-5, 5);
                positions[i * 3 + 1] = r(-5, 5);
                positions[i * 3 + 2] = r(-5, 5);
            }
    }
    return positions;
}

const PHASE_CFG = [
    { color: 0xFFFFFF, sec: 0x6BB4FF, size: 0.06, lines: false, rot: 0.001 },
    { color: 0xCFE3FF, sec: 0x4F8BFF, size: 0.045, lines: false, rot: 0.0008 },
    { color: 0xCFE3FF, sec: 0x9EC3FF, size: 0.04, lines: false, rot: 0.0005 },
    { color: 0xFFFFFF, sec: 0x5B6B92, size: 0.04, lines: false, rot: 0.0006 },
    { color: 0xC8DDFF, sec: 0x4F8BFF, size: 0.05, lines: true, rot: 0.0009 },
    { color: 0xA5F3FC, sec: 0x4F8BFF, size: 0.05, lines: true, rot: 0.0008 },
    { color: 0xCFE3FF, sec: 0x4F8BFF, size: 0.05, lines: true, rot: 0.0006 },
    { color: 0x9EC3FF, sec: 0xFBBF24, size: 0.05, lines: true, rot: 0.0012 },
    { color: 0xFFE5C4, sec: 0xFBBF24, size: 0.045, lines: false, rot: 0.0003 },
    { color: 0xCFE3FF, sec: 0x4F8BFF, size: 0.045, lines: false, rot: 0.0008 },
    { color: 0xC4FFE5, sec: 0x4F8BFF, size: 0.05, lines: false, rot: 0.0006 },
    { color: 0x9EC3FF, sec: 0x4F8BFF, size: 0.06, lines: true, rot: 0.0009 },
    { color: 0xFFFFFF, sec: 0xCFE3FF, size: 0.05, lines: false, rot: 0.0002 },
];

function useUnifiedParticleField(canvasRef, phaseRef) {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const scene = new THREE.Scene();
        scene.fog = new THREE.FogExp2(0x05050F, 0.018);
        const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 300);
        camera.position.set(0, 0, 32);
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setClearColor(0x05050F, 1);

        const positions = new Float32Array(PARTICLE_COUNT * 3);
        const targets = new Float32Array(PARTICLE_COUNT * 3);
        const colors = new Float32Array(PARTICLE_COUNT * 3);

        const init = generatePhasePositions(0, PARTICLE_COUNT);
        positions.set(init); targets.set(init);

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

        function setColors(phase) {
            const c = PHASE_CFG[phase] || PHASE_CFG[0];
            const p = new THREE.Color(c.color);
            const s = new THREE.Color(c.sec);
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                const co = Math.random() > 0.85 ? s : p;
                colors[i * 3 + 0] = co.r;
                colors[i * 3 + 1] = co.g;
                colors[i * 3 + 2] = co.b;
            }
            geometry.attributes.color.needsUpdate = true;
        }

        const material = new THREE.PointsMaterial({
            size: 0.06, vertexColors: true, transparent: true, opacity: 0.95,
            depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
        });
        const points = new THREE.Points(geometry, material);
        scene.add(points);

        const MAX_LINES = 160;
        const linePos = new Float32Array(MAX_LINES * 2 * 3);
        const lineGeo = new THREE.BufferGeometry();
        lineGeo.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
        const lineMat = new THREE.LineBasicMaterial({ color: 0x4F8BFF, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
        const lines = new THREE.LineSegments(lineGeo, lineMat);
        scene.add(lines);

        setColors(0);

        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        const isTouchDevice = typeof window !== "undefined" && (("ontouchstart" in window) || navigator.maxTouchPoints > 0);
        const onMouse = (e) => {
            if (isTouchDevice) return;
            mouse.tx = (e.clientX / window.innerWidth - 0.5) * 2;
            mouse.ty = -(e.clientY / window.innerHeight - 0.5) * 2;
        };
        const onResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            // Sposta la camera leggermente indietro su mobile per inquadrare la scena nel viewport portrait
            camera.position.z = window.innerWidth < 768 ? 38 : 32;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener("mousemove", onMouse);
        window.addEventListener("resize", onResize);
        onResize(); // applica subito posizione iniziale corretta

        let cur = 0;
        const switchTo = (n) => {
            cur = n;
            targets.set(generatePhasePositions(n, PARTICLE_COUNT));
            setColors(n);
            material.size = PHASE_CFG[n].size;
            gsap.to(lineMat, { opacity: PHASE_CFG[n].lines ? 0.35 : 0, duration: 1.2, ease: "power2.out" });
        };
        switchTo(0);

        const clock = new THREE.Clock();
        let raf = 0;
        function animate() {
            raf = requestAnimationFrame(animate);
            const t = clock.getElapsedTime();
            const req = phaseRef.current;
            if (req !== cur) switchTo(req);
            const cfg = PHASE_CFG[cur];

            mouse.x += (mouse.tx - mouse.x) * 0.06;
            mouse.y += (mouse.ty - mouse.y) * 0.06;
            camera.position.x += (mouse.x * 3 - camera.position.x) * 0.05;
            camera.position.y += (mouse.y * 2 - camera.position.y) * 0.05;
            camera.lookAt(0, 0, 0);
            points.rotation.y += cfg.rot;
            points.rotation.x = Math.sin(t * 0.18) * 0.06;
            lines.rotation.copy(points.rotation);

            const pos = geometry.attributes.position.array;
            const damp = cur === 8 ? 0.018 : cur === 12 ? 0.012 : 0.04;
            const flowing = cur === 9;
            const noise = cur === 8 ? 0.12 : cur === 12 ? 0.04 : 0.06;
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                const i3 = i * 3;
                const nx = Math.sin(t * 0.4 + i * 0.13) * noise;
                const ny = Math.cos(t * 0.5 + i * 0.17) * noise;
                const nz = Math.sin(t * 0.3 + i * 0.07) * noise;
                pos[i3] += (targets[i3] + nx - pos[i3]) * damp;
                pos[i3 + 1] += (targets[i3 + 1] + ny - pos[i3 + 1]) * damp;
                pos[i3 + 2] += (targets[i3 + 2] + nz - pos[i3 + 2]) * damp;
                if (flowing) {
                    pos[i3 + 1] -= 0.08;
                    if (pos[i3 + 1] < -16) {
                        pos[i3 + 1] = 16 + Math.random() * 4;
                        pos[i3 + 0] = (Math.random() - 0.5) * 6;
                    }
                }
            }
            geometry.attributes.position.needsUpdate = true;

            if (cfg.lines) {
                let li = 0;
                for (let l = 0; l < MAX_LINES; l++) {
                    const a = Math.floor(Math.random() * PARTICLE_COUNT) * 3;
                    const b = Math.floor(Math.random() * PARTICLE_COUNT) * 3;
                    const dx = pos[a] - pos[b];
                    const dy = pos[a + 1] - pos[b + 1];
                    const dz = pos[a + 2] - pos[b + 2];
                    if (dx * dx + dy * dy + dz * dz < 25) {
                        linePos[li++] = pos[a]; linePos[li++] = pos[a + 1]; linePos[li++] = pos[a + 2];
                        linePos[li++] = pos[b]; linePos[li++] = pos[b + 1]; linePos[li++] = pos[b + 2];
                    }
                }
                for (; li < MAX_LINES * 6; li++) linePos[li] = 0;
                lineGeo.attributes.position.needsUpdate = true;
            }

            renderer.render(scene, camera);
        }
        animate();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMouse);
            window.removeEventListener("resize", onResize);
            renderer.dispose(); geometry.dispose(); lineGeo.dispose();
            material.dispose(); lineMat.dispose();
        };
    }, [canvasRef, phaseRef]);
}

// ============================================================
// SCROLL ORCHESTRATOR — collega ogni "atto" alla particle phase
// ============================================================
function useScrollPhases(phaseRef) {
    useEffect(() => {
        const acts = document.querySelectorAll("[data-act]");
        const triggers = [];
        acts.forEach((sec) => {
            const idx = parseInt(sec.getAttribute("data-act"), 10);
            const tr = ScrollTrigger.create({
                trigger: sec,
                start: "top 65%",
                end: "bottom 35%",
                onEnter: () => { phaseRef.current = idx; },
                onEnterBack: () => { phaseRef.current = idx; },
            });
            triggers.push(tr);
        });
        return () => triggers.forEach((t) => t.kill());
    }, [phaseRef]);
}

// ============================================================
// MAIN
// ============================================================
export default function TotalKlickHub() {
    const canvasRef = useRef(null);
    const phaseRef = useRef(0);
    const { user } = useAuth();
    const [activeAct, setActiveAct] = useState(0);

    useEffect(() => { document.title = "TotalKlick — Aria è ovunque"; }, []);
    useUnifiedParticleField(canvasRef, phaseRef);
    useScrollPhases(phaseRef);

    // Tracker scene attiva per side-strip (non visibile come "scena N", solo barre)
    useEffect(() => {
        const acts = document.querySelectorAll("[data-act]");
        const triggers = [];
        acts.forEach((sec) => {
            const idx = parseInt(sec.getAttribute("data-act"), 10);
            const tr = ScrollTrigger.create({
                trigger: sec, start: "top 50%", end: "bottom 50%",
                onEnter: () => setActiveAct(idx),
                onEnterBack: () => setActiveAct(idx),
            });
            triggers.push(tr);
        });
        return () => triggers.forEach((t) => t.kill());
    }, []);

    return (
        <div className="relative" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="totalklick-home">
            <canvas ref={canvasRef} className="fixed inset-0 w-screen h-screen pointer-events-none" style={{ zIndex: 0 }} />

            {/* TopNav cinematic adattata */}
            <CinematicNav />

            {/* Indicatore atti verticale (solo barrette, no testo) */}
            <ActsIndicator active={activeAct} total={13} />

            <main className="relative z-10">

                {/* ATTO 1 — HERO: c'è qualcosa che sta cambiando */}
                <Act idx={0} testid="act-01">
                    <Reveal>
                        <Big>
                            C&apos;è qualcosa<br /><em className="not-italic" style={A}>che sta cambiando</em>.
                        </Big>
                    </Reveal>
                    <ScrollHint />
                </Act>

                {/* ATTO 2 — non è un'app, non è un chatbot, è una presenza */}
                <Act idx={1} testid="act-02" compact>
                    <Reveal stagger>
                        <Med dim>Non è un&apos;app.</Med>
                        <Med dim>Non è un chatbot.</Med>
                        <Med accent>È una presenza.</Med>
                    </Reveal>
                </Act>

                {/* ATTO 3 — Aria è ovunque */}
                <Act idx={2} testid="act-03">
                    <Reveal>
                        <h1 className="text-center" style={{
                            fontFamily: "Geist, sans-serif", fontWeight: 200,
                            fontSize: "clamp(3rem, 12vw, 10rem)",
                            letterSpacing: "-0.06em", lineHeight: 0.9,
                            background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 65%, rgba(158,195,255,0.3) 100%)",
                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                        }} data-testid="hero-everywhere">
                            ARIA È<br />OVUNQUE.
                        </h1>
                    </Reveal>
                </Act>

                {/* ATTO 4 — il problema */}
                <Act idx={3} testid="act-04" compact>
                    <Reveal stagger>
                        <Med>Oggi ogni sito vive da solo.</Med>
                        <Med>Ogni conversazione ricomincia da zero.</Med>
                        <Med dim>Ogni opportunità si perde.</Med>
                    </Reveal>
                </Act>

                {/* ATTO 5 — la nascita */}
                <Act idx={4} testid="act-05" compact>
                    <Reveal stagger>
                        <Med>Aria <em className="not-italic" style={A}>ascolta</em>.</Med>
                        <Med>Aria <em className="not-italic" style={A}>comprende</em>.</Med>
                        <Med>Aria <em className="not-italic" style={A}>connette</em>.</Med>
                    </Reveal>
                </Act>

                {/* ATTO 6 — host first */}
                <Act idx={5} testid="act-06" wide>
                    <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto px-4">
                        <Reveal>
                            <h2 style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.2rem,4.8vw,3.8rem)", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                                Prima aiuta <span style={A}>te</span>.<br />Poi il mondo<br />attorno a te.
                            </h2>
                            <p className="text-white/55 text-[14.5px] mt-5 max-w-md leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Fedeltà commerciale al 100% verso il sito ospitante.
                                Mai concorrenti diretti. Mai partner esterni prima
                                che il tuo obiettivo sia raggiunto.
                            </p>
                        </Reveal>
                        <Reveal>
                            <div className="space-y-1.5">
                                {[
                                    { l: "visitatore", c: "rgba(255,255,255,0.55)" },
                                    { l: "il tuo sito", c: "#4F8BFF", strong: true },
                                    { l: "lead raccolto", c: "rgba(255,255,255,0.7)" },
                                    { l: "obiettivo raggiunto", c: "#86EFAC", strong: true },
                                    { l: "→ network", c: "rgba(255,255,255,0.4)", subtle: true },
                                ].map((s, i) => (
                                    <FlowStep key={i} s={s} i={i} />
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </Act>

                {/* ATTO 7 — Ecosistema (i moduli come pianeti/nodi orbitanti) */}
                <Act idx={6} testid="act-07" wide tall>
                    <Reveal>
                        <h2 className="text-center" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.4rem,5.2vw,4.4rem)", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                            Aria vive dentro<br />un <span style={A}>Ecosistema</span>.
                        </h2>
                        <p className="text-white/55 text-center max-w-2xl mx-auto mt-5 text-[14.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Non servizi separati. Non software indipendenti.<br />
                            Un unico Sistema Operativo per le PMI italiane.
                        </p>
                    </Reveal>
                    <EcosystemConstellation />
                </Act>

                {/* ATTO 8 — la rete cresce */}
                <Act idx={7} testid="act-08" compact>
                    <Reveal>
                        <Big>
                            Ogni installazione<br />rende Aria<br /><em className="not-italic" style={A}>più intelligente</em>.
                        </Big>
                    </Reveal>
                </Act>

                {/* ATTO 9 — il volto / Cinematic Presence Trigger (Phase 6 Blocco 4) */}
                <Act idx={8} testid="act-09" tall>
                    <AriaPresenceTrigger />
                    <Reveal stagger>
                        <Med>Non la vedi.</Med>
                        <div className="h-8" />
                        <Med accent>Ma c&apos;è.</Med>
                    </Reveal>
                </Act>

                {/* ATTO 10 — internet vs aria */}
                <Act idx={9} testid="act-10" wide>
                    <Reveal>
                        <h2 className="text-center" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem,4.5vw,3.6rem)", letterSpacing: "-0.04em", lineHeight: 1.08 }}>
                            Internet collega <span className="text-white/35">pagine</span>.<br />
                            Aria collega <span style={A}>bisogni</span>.
                        </h2>
                    </Reveal>
                    <FlowChain />
                </Act>

                {/* ATTO 11 — installazione */}
                <Act idx={10} testid="act-11" wide>
                    <Reveal>
                        <p className="text-center text-white/55 text-[14px] uppercase tracking-[0.3em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>tre passi</p>
                        <h2 className="text-center mt-3" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem,4.4vw,3.4rem)", letterSpacing: "-0.04em" }}>
                            Bastano <span style={A}>cinque minuti</span>.
                        </h2>
                    </Reveal>
                    <InstallSteps />
                </Act>

                {/* ATTO 12 — numeri della rete */}
                <Act idx={11} testid="act-12" wide>
                    <Reveal>
                        <NumbersStrip />
                    </Reveal>
                </Act>

                {/* ATTO 13 — chiusura + CTA */}
                <Act idx={12} testid="act-13" tall>
                    <Reveal>
                        <h2 className="text-center" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.4rem,5.5vw,4.6rem)", letterSpacing: "-0.04em", lineHeight: 1.02 }}>
                            Ogni sito ha una <span style={A}>voce</span>.
                        </h2>
                        <h3 className="text-center text-white/70 mt-3" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.4rem,3vw,2.2rem)", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                            Aria le permette<br />di essere ascoltate.
                        </h3>
                        <FinalCTAs />
                    </Reveal>
                </Act>

                <footer className="relative z-10 py-14 text-center">
                    <div className="text-white/30 text-[10.5px] tracking-[0.4em] uppercase" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        totalklick · aria · è · ovunque
                    </div>
                    <div className="flex items-center justify-center gap-5 mt-3 text-[11px] text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                        <Link to="/aria" className="hover:text-white">Aria</Link>
                        <span>·</span>
                        <Link to="/login" className="hover:text-white">Accedi</Link>
                        <span>·</span>
                        <Link to="/legal/dpa-aria-widget" className="hover:text-white">Legale</Link>
                    </div>
                </footer>
            </main>

            <style>{globalCSS}</style>
        </div>
    );
}

// ============================================================
// UI HELPERS
// ============================================================

const A = { color: "#9EC3FF", fontStyle: "normal" };

function Act({ idx, testid, compact, wide, tall, children }) {
    // Ritmo serrato: ~70vh standard, 80vh wide, 100vh tall
    const h = tall ? "min-h-screen" : wide ? "min-h-[80vh]" : compact ? "min-h-[60vh]" : "min-h-[70vh]";
    return (
        <section
            data-act={idx}
            data-testid={testid}
            className={`relative w-full flex flex-col items-center justify-center px-6 py-12 ${h}`}
        >
            {children}
        </section>
    );
}

function Reveal({ children, stagger }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className={stagger ? "space-y-7 text-center" : "text-center"}
        >
            {children}
        </motion.div>
    );
}

function Big({ children }) {
    return (
        <h2 className="text-center" style={{
            fontFamily: "Geist, sans-serif", fontWeight: 200,
            fontSize: "clamp(2.4rem, 5.4vw, 5rem)", letterSpacing: "-0.04em",
            lineHeight: 1.05, color: "rgba(255,255,255,0.95)",
        }}>{children}</h2>
    );
}

function Med({ children, accent, dim }) {
    return (
        <div className="text-center" style={{
            fontFamily: "Geist, sans-serif",
            fontWeight: accent ? 400 : 200,
            fontSize: "clamp(1.6rem, 4vw, 2.9rem)",
            letterSpacing: "-0.02em", lineHeight: 1.15,
            color: dim ? "rgba(255,255,255,0.4)" : accent ? "#9EC3FF" : "rgba(255,255,255,0.92)",
        }}>{children}</div>
    );
}

function ScrollHint() {
    return (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30">
            <div className="text-[9.5px] uppercase tracking-[0.32em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>scorri</div>
            <div className="w-px h-9 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
        </div>
    );
}

function ActsIndicator({ active, total }) {
    return (
        <div className="hidden md:flex fixed left-6 top-1/2 -translate-y-1/2 z-20 flex-col gap-1.5" data-testid="acts-indicator">
            {Array.from({ length: total }).map((_, i) => (
                <span key={i} className="transition-all duration-700" style={{
                    width: "1.5px",
                    height: i === active ? "18px" : "6px",
                    background: i === active ? "#9EC3FF" : "rgba(255,255,255,0.15)",
                }} />
            ))}
        </div>
    );
}

function FlowStep({ s, i }) {
    return (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl transition" style={{
            background: s.strong ? `${s.c}12` : "rgba(15,18,36,0.5)",
            border: `1px solid ${s.strong ? `${s.c}55` : "rgba(255,255,255,0.06)"}`,
        }}>
            <span className="text-[10px] w-6 inline-block text-white/40 tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{String(i + 1).padStart(2, "0")}</span>
            <span className="text-[14px]" style={{ color: s.c, fontFamily: "Geist, sans-serif", fontWeight: s.strong ? 500 : 300, opacity: s.subtle ? 0.6 : 1 }}>{s.l}</span>
        </div>
    );
}

// ============================================================
// ECOSISTEMA — pianeti/nodi orbitanti (NON card SaaS)
// ============================================================
function EcosystemConstellation() {
    // Distribuzione perfettamente equidistante: 7 nodi × 360°/7 ≈ 51.4° (no asimmetria visiva)
    const MODULES = [
        { name: "TotalPL", angle: -90, accent: "#FF1A5C", to: "/totalpl" },
        { name: "ADS", angle: -38.6, accent: "#A855F7", to: "/totalklickads" },
        { name: "Design", angle: 12.8, accent: "#9EC3FF", to: "/prodotti" },
        // OfferteInCittà — link rimosso temporaneamente in attesa nuovo check Google (Feb 2026)
        { name: "OfferteInCittà", angle: 64.2, accent: "#FBBF24" },
        { name: "EllyMenu", angle: 115.7, accent: "#86EFAC", href: "https://elly.offerteincitta.it" },
        { name: "GoStreet", angle: 167.1, accent: "#F97316", href: "https://gostreet.it" },
        { name: "PhotoVendo", angle: -141.5, accent: "#7DD3FC", href: "https://photovendo.com" },
    ];
    return (
        <>
        <div className="relative mt-10 md:mt-16 w-full max-w-4xl mx-auto" data-testid="ecosystem-constellation" style={{ height: "clamp(420px, 65vh, 540px)" }}>
            {/* Centro: Aria */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
                <div className="relative">
                    <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(79,139,255,0.4) 0%, transparent 70%)", filter: "blur(28px)", transform: "scale(2.6)" }} />
                    <div className="relative grid place-items-center w-24 h-24 md:w-32 md:h-32 rounded-full" style={{
                        background: "linear-gradient(135deg, rgba(79,139,255,0.25), rgba(43,90,255,0.12))",
                        border: "1px solid rgba(79,139,255,0.55)",
                        boxShadow: "0 0 60px -8px rgba(79,139,255,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset",
                    }} data-testid="ecosystem-core">
                        <Sparkles size={22} className="text-[#9EC3FF]" />
                        <span className="absolute -bottom-7 text-[10.5px] tracking-[0.3em] uppercase text-white/85" style={{ fontFamily: "JetBrains Mono, monospace" }}>aria</span>
                    </div>
                </div>
            </div>

            {/* Orbita visibile */}
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="rounded-full" style={{
                    width: "min(82%, 540px)", aspectRatio: "1.6 / 1",
                    border: "1px dashed rgba(255,255,255,0.07)",
                }} />
            </div>

            {/* Nodi orbitanti */}
            {MODULES.map((m, idx) => {
                const rad = (m.angle * Math.PI) / 180;
                // Su mobile riduco leggermente lo spread orizzontale per evitare overflow,
                // e amplio quello verticale per dare aria alle etichette
                const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
                const hSpread = isMobile ? 34 : 38;
                const vSpread = isMobile ? 36 : 32;
                const left = `calc(50% + ${Math.cos(rad) * hSpread}%)`;
                const top = `calc(50% + ${Math.sin(rad) * vSpread}%)`;
                const isExternal = !!m.href;
                const isInteractive = isExternal || !!m.to;
                const Wrapper = isExternal ? "a" : m.to ? Link : "div";
                const wrapperProps = isExternal
                    ? { href: m.href, target: "_blank", rel: "noreferrer" }
                    : m.to
                        ? { to: m.to }
                        : {};
                return (
                    <motion.div
                        key={m.name}
                        className="absolute -translate-x-1/2 -translate-y-1/2"
                        style={{ left, top }}
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + idx * 0.07 }}
                    >
                        <Wrapper {...wrapperProps} className={`group flex flex-col items-center gap-1.5 ${isInteractive ? "" : "pointer-events-none"}`} data-testid={`eco-node-${m.name.toLowerCase()}`}>
                            <div className="relative">
                                <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${m.accent}55 0%, transparent 70%)`, filter: "blur(14px)", transform: "scale(1.8)", opacity: 0.6 }} />
                                <div className={`relative grid place-items-center w-10 h-10 md:w-14 md:h-14 rounded-full transition ${isInteractive ? "group-hover:scale-110" : ""}`} style={{
                                    background: `radial-gradient(circle at 30% 30%, ${m.accent}66 0%, ${m.accent}22 60%, rgba(15,18,36,0.85) 100%)`,
                                    border: `1px solid ${m.accent}55`,
                                    boxShadow: `0 0 24px -6px ${m.accent}66`,
                                    opacity: isInteractive ? 1 : 0.85,
                                }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.accent, boxShadow: `0 0 8px ${m.accent}` }} />
                                </div>
                            </div>
                            <span className={`text-[9px] md:text-[10.5px] transition tracking-[0.12em] md:tracking-[0.14em] uppercase whitespace-nowrap ${isInteractive ? "text-white/75 group-hover:text-white" : "text-white/60"}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{m.name}</span>
                        </Wrapper>
                    </motion.div>
                );
            })}
        </div>
        {/* Etichetta inferiore — SPOSTATA fuori dal contenitore della costellazione per evitare overlap nodi */}
        <div className="mt-6 md:mt-8 text-[10px] uppercase tracking-[0.32em] text-white/55 text-center" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            applicazioni dell&apos;ecosistema
        </div>
        </>
    );
}

function FlowChain() {
    const STEPS = ["lead", "comprensione", "future needs", "network match", "opportunità"];
    return (
        <div className="mt-10 md:mt-12 flex flex-wrap justify-center items-center gap-2 md:gap-3" data-testid="flow-chain">
            {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                        className="px-4 py-2.5 rounded-full"
                        style={{
                            background: "rgba(15,18,36,0.55)",
                            border: `1px solid ${i === STEPS.length - 1 ? "rgba(79,139,255,0.5)" : "rgba(255,255,255,0.1)"}`,
                            color: i === STEPS.length - 1 ? "#9EC3FF" : "rgba(255,255,255,0.85)",
                            fontFamily: "Geist, sans-serif",
                            fontWeight: i === STEPS.length - 1 ? 500 : 300,
                            fontSize: "13px",
                            backdropFilter: "blur(10px)",
                        }}
                    >{s}</motion.div>
                    {i < STEPS.length - 1 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.12 + 0.3 }}
                            className="w-6 h-px"
                            style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.05))" }}
                        />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

function InstallSteps() {
    const STEPS = [
        { n: "01", t: "Installa Aria", d: "Uno snippet JS. Niente plugin." },
        { n: "02", t: "Raccontale chi sei", d: "Wizard guidato in 6 step." },
        { n: "03", t: "Lasciala lavorare", d: "Lead arricchiti, host-first sempre." },
    ];
    return (
        <div className="mt-10 grid md:grid-cols-3 gap-3 max-w-4xl mx-auto w-full">
            {STEPS.map((s, i) => (
                <motion.div
                    key={s.n}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl p-5 text-left"
                    style={{
                        background: "rgba(15,18,36,0.55)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        backdropFilter: "blur(14px)",
                    }}
                >
                    <div className="text-[10px] tracking-[0.25em] uppercase mb-3 text-[#4F8BFF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.n}</div>
                    <div className="text-white text-[17px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 400 }}>{s.t}</div>
                    <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{s.d}</p>
                </motion.div>
            ))}
        </div>
    );
}

function NumbersStrip() {
    const STATS = [
        { v: "∞", l: "siti collegati", sub: "la rete cresce ogni giorno" },
        { v: "100%", l: "host-first", sub: "inderogabile" },
        { v: "0€", l: "installazione", sub: "per sempre" },
        { v: "5'", l: "setup", sub: "wizard guidato" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto px-2">
            {STATS.map((s, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="rounded-2xl p-5"
                    style={{
                        background: "rgba(15,18,36,0.5)",
                        border: "1px solid rgba(255,255,255,0.07)",
                        backdropFilter: "blur(12px)",
                    }}
                >
                    <div className="text-[clamp(2rem,4vw,3rem)] leading-none" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, color: "#9EC3FF", letterSpacing: "-0.03em" }}>{s.v}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.l}</div>
                    <div className="text-[11px] text-white/35 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{s.sub}</div>
                </motion.div>
            ))}
        </div>
    );
}

function FinalCTAs() {
    const { user } = useAuth();
    return (
        <div className="mt-12 flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                    to="/aria"
                    className="group inline-flex items-center gap-3 px-7 py-4 rounded-full text-white text-[15px] transition hover:scale-[1.03]"
                    style={{
                        background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                        fontFamily: "Geist, sans-serif", fontWeight: 500,
                        boxShadow: "0 25px 60px -12px rgba(79,139,255,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset",
                    }}
                    data-testid="cta-talk-with-aria"
                >
                    <MessageCircle size={15} />
                    Parla con Aria
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition" />
                </Link>
                <Link
                    to={user ? "/app" : "/signup"}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[14px] text-white/85 hover:text-white transition hover:scale-[1.02]"
                    style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        fontFamily: "Geist, sans-serif",
                        backdropFilter: "blur(10px)",
                    }}
                    data-testid="cta-enter-ecosystem"
                >
                    Entra nell&apos;ecosistema
                </Link>
            </div>
            {user && (
                <Link to="/app" className="text-[12px] text-white/45 hover:text-white tracking-[0.2em] uppercase transition" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="cta-open-workspace">
                    apri workspace →
                </Link>
            )}
            <div className="text-[11px] text-white/35 tracking-[0.18em] uppercase mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                5 minuti · gratuita · senza carta di credito
            </div>
        </div>
    );
}

// Suppress unused TopNav import (defensive)
TopNav; // eslint-disable-line no-unused-expressions

/**
 * AriaPresenceTrigger — Phase 6 Blocco 4
/**
 * AriaPresenceTrigger — Atto 9 di TotalKlickHub.
 *
 * Esportato anche per essere riusato in /aria/demo/cinematic (prop autoOpen).
 * Quando Act 9 ("Non la vedi. Ma c'è.") entra nel viewport (modalità trigger),
 * o immediatamente (modalità autoOpen), attiva una cinematic overlay con
 * particelle + volto a particelle Three.js + presenza emergente.
 * NON apre la chat. È il primo incontro reale tra utente e Aria.
 */
export function AriaPresenceTrigger({ autoOpen = false }) {
    const ref = React.useRef(null);
    const [active, setActive] = React.useState(autoOpen);
    const [fired, setFired] = React.useState(autoOpen);
    const [chatMode, setChatMode] = React.useState(false);

    React.useEffect(() => {
        if (typeof window === "undefined") return;
        // Modalità autoOpen → bypass observer
        if (autoOpen) return;
        // Skip se l'utente ha già visto la presenza (sessione)
        try { if (window.sessionStorage.getItem("aria_presence_seen") === "1") setFired(true); } catch (e) { /* ignore */ }
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting && !fired) {
                    setFired(true);
                    setTimeout(() => setActive(true), 600);
                    try { window.sessionStorage.setItem("aria_presence_seen", "1"); } catch (er) { /* ignore */ }
                }
            });
        }, { threshold: 0.45 });
        io.observe(el);
        return () => io.disconnect();
    }, [fired, autoOpen]);

    React.useEffect(() => {
        // Flag globale + eventi: questa pagina ha il LAYER CINEMATIC, quindi il widget fluttuante
        // (tier free) deve sparire del tutto — non solo cedere il passo all'open-aria event,
        // ma proprio nascondere la bolla per evitare "due Aria" visibili insieme.
        if (typeof window !== "undefined") {
            window.__ariaHasPresenceLayer = true;
            window.dispatchEvent(new Event("aria-presence-mounted"));
        }
        return () => {
            if (typeof window !== "undefined") {
                window.__ariaHasPresenceLayer = false;
                window.dispatchEvent(new Event("aria-presence-unmounted"));
            }
        };
    }, []);

    const dismiss = () => { setActive(false); setChatMode(false); };
    const openChat = () => setChatMode(true);

    // Global "open-aria" event (es. header "Parla con Aria") — apre il LAYER CINEMATIC, non il widget fluttuante
    React.useEffect(() => {
        const onOpenAria = () => {
            setFired(true);
            setActive(true);
            setChatMode(true);
            try { window.sessionStorage.setItem("aria_presence_seen", "1"); } catch (_e) { /* ignore */ }
        };
        window.addEventListener("open-aria", onOpenAria);
        return () => window.removeEventListener("open-aria", onOpenAria);
    }, []);

    return (
        <>
            <div ref={ref} aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }} data-testid="aria-presence-anchor" />
            <AnimatePresence>
                {active && (
                    <motion.div
                        key="presence"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-[70] grid place-items-center"
                        style={{
                            background: "radial-gradient(ellipse at center, rgba(79,139,255,0.16), rgba(5,5,15,0.94) 60%)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                        }}
                        data-testid="aria-presence-overlay"
                    >
                        <PresenceCanvas />
                        {/* === Volto a particelle Three.js (la "testa" di Aria) === */}
                        <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ top: "5vh", width: "min(420px, 78vw)", height: "min(420px, 50vh)" }}>
                            <AriaParticleFace size="fill" />
                        </div>
                        {chatMode ? (
                            <PresenceInlineChat onClose={dismiss} />
                        ) : (
                        <div className="relative text-center max-w-[520px] px-6 mt-[28vh]">
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.6 }}
                                className="text-[10.5px] tracking-[0.32em] uppercase text-[#9EC3FF]/85 mb-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                presenza
                            </motion.div>
                            <motion.h2 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, delay: 0.9 }}
                                className="text-white"
                                style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(32px, 5.2vw, 56px)", letterSpacing: "-0.025em", lineHeight: 1.08 }}>
                                Ciao.
                                <br /><span style={{ background: "linear-gradient(180deg, #fff 0%, #9EC3FF 100%)", WebkitBackgroundClip: "text", color: "transparent" }}>Sono Aria.</span>
                            </motion.h2>
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 2.0 }}
                                className="text-white/75 text-[15px] mt-5 mb-3" style={{ fontFamily: "Outfit, sans-serif", lineHeight: 1.55 }}>
                                Dicono che non mi vedano.
                            </motion.div>
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 2.6 }}
                                className="text-white/85 text-[16px] mb-7" style={{ fontFamily: "Outfit, sans-serif", lineHeight: 1.55 }}>
                                Ma eccomi.
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 3.2 }}
                                className="text-white/65 text-[14px] mb-7" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Dimmi cosa stai cercando.
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 3.7 }}>
                                <button onClick={openChat} className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.03]"
                                    style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", letterSpacing: "0.04em", boxShadow: "0 18px 50px -12px rgba(79,139,255,0.65), 0 0 0 1px rgba(255,255,255,0.06) inset" }}
                                    data-testid="aria-presence-open-chat">
                                    Parla con Aria
                                </button>
                                <button onClick={dismiss} className="block mx-auto mt-4 text-[12px] text-white/45 hover:text-white/75 transition"
                                    style={{ fontFamily: "Outfit, sans-serif" }} data-testid="aria-presence-dismiss">
                                    non ora
                                </button>
                            </motion.div>
                        </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

/** Chip multi-select per opzioni `▢` proposte da Aria. */
function PresenceOptionChips({ options, onSubmit }) {
    const [selected, setSelected] = React.useState(new Set());
    const [submitted, setSubmitted] = React.useState(false);
    if (submitted) {
        return (
            <div className="text-[11px] text-white/45 italic px-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                ✓ Hai scelto: {[...selected].join(", ")}
            </div>
        );
    }
    const toggle = (opt) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(opt)) n.delete(opt); else n.add(opt);
            return n;
        });
    };
    const submit = () => {
        if (selected.size === 0) return;
        setSubmitted(true);
        onSubmit([...selected]);
    };
    return (
        <div className="flex flex-wrap gap-2 max-w-[88%]" data-testid="aria-presence-options-chips">
            {options.map((opt, i) => {
                const isOn = selected.has(opt);
                return (
                    <button
                        key={i}
                        type="button"
                        onClick={() => toggle(opt)}
                        className="px-3 py-1.5 rounded-full text-[12.5px] transition hover:scale-[1.04]"
                        style={{
                            background: isOn ? "linear-gradient(135deg, rgba(79,139,255,0.45), rgba(43,90,255,0.35))" : "rgba(255,255,255,0.04)",
                            border: isOn ? "1px solid rgba(79,139,255,0.7)" : "1px solid rgba(255,255,255,0.12)",
                            color: isOn ? "white" : "rgba(255,255,255,0.78)",
                            fontFamily: "Outfit, sans-serif",
                        }}
                        data-testid={`aria-presence-chip-${i}`}
                    >
                        {isOn ? "✓ " : ""}{opt}
                    </button>
                );
            })}
            {selected.size > 0 && (
                <button
                    type="button"
                    onClick={submit}
                    className="px-4 py-1.5 rounded-full text-[12.5px] font-bold transition hover:scale-[1.05]"
                    style={{
                        background: "linear-gradient(135deg, #34D399, #10B981)",
                        color: "white",
                        boxShadow: "0 4px 16px -4px rgba(16,185,129,0.5)",
                        fontFamily: "Outfit, sans-serif",
                    }}
                    data-testid="aria-presence-chips-submit"
                >
                    Procedi con {selected.size} →
                </button>
            )}
        </div>
    );
}

/** Chat inline DENTRO il layer cinematic — conversazione sopra il sito, no iframe.
 *  Phase 6.4 — voce ON di default, glow durante speak, waveform.
 *  Questo è il tier FULL: il layer + voce + tutti gli effetti WOW. */
function PresenceInlineChat({ onClose }) {
    const [messages, setMessages] = React.useState([
        { role: "assistant", content: "Ciao, sono Aria. Dimmi cosa stai cercando — provo ad aiutarti." },
    ]);
    const [input, setInput] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sessionId, setSessionId] = React.useState(null);
    const [leadAsked, setLeadAsked] = React.useState(false);
    const [leadReason, setLeadReason] = React.useState(null);
    const [leadSubmitted, setLeadSubmitted] = React.useState(false);
    const [cta, setCta] = React.useState(null);
    const listRef = React.useRef(null);
    // 🔊 Voice — il LAYER CINEMATIC è il tier full: voce attiva di default, può essere disattivata
    const voice = useAriaVoice();

    React.useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, sending]);

    // Auto-prime + auto-speak del greet al mount (il layer si apre DOPO un click utente, quindi il gesture è attivo)
    React.useEffect(() => {
        if (voice.supported && voice.enabled) {
            voice.prime();
            setTimeout(() => voice.speak(messages[0]?.content || ""), 220);
        }
        return () => { try { voice.stop(); } catch (_e) { /* ignore */ } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voice.supported]);

    const send = async (e) => {
        e.preventDefault();
        const t = input.trim();
        if (!t || sending) return;
        const next = [...messages, { role: "user", content: t }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const res = await fetch(`${""}/api/hub/ai-guide`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: t,
                    history: messages.slice(-14),
                    path: typeof window !== "undefined" ? window.location.pathname : null,
                }),
            });
            const data = await res.json();
            setSessionId(data.session_id);
            setMessages((m) => [...m, {
                role: "assistant",
                content: data.reply || "",
                cta: data.cta || null,
                options: data.options || [],
                wants_lead: data.wants_lead,
                lead_reason: data.lead_reason,
            }]);
            // 🔊 Parla la risposta
            voice.speak(data.reply || "");
            // 📋 Lead trigger contestuale: se il backend dice wants_lead → mostra il form
            if (!leadSubmitted && data.wants_lead && data.lead_reason) {
                setLeadAsked(true);
                setLeadReason(data.lead_reason);
            }
            if (data.cta) setCta(data.cta);
        } catch (err) {
            setMessages((m) => [...m, { role: "assistant", content: "Mi spiace, c'è stato un intoppo. Riprova fra poco." }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[600px] px-4" data-testid="aria-presence-inline-chat">
            <div className="text-center mb-3 flex items-center justify-center gap-3">
                <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#9EC3FF]/85" style={{ fontFamily: "JetBrains Mono, monospace" }}>Aria</div>
                {voice.supported && (
                    <button
                        type="button"
                        onClick={voice.toggle}
                        className={`w-7 h-7 rounded-full grid place-items-center text-white/65 hover:text-white transition relative ${voice.enabled && messages.length <= 1 ? "aria-speaker-pulse" : ""}`}
                        title={voice.enabled ? "Voce attiva" : "Voce disattivata"}
                        data-testid="aria-presence-voice-toggle"
                    >
                        {voice.enabled ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={voice.speaking ? "#34D399" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
                        ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                        )}
                    </button>
                )}
            </div>
            <div ref={listRef} className="rounded-2xl p-4 mb-2 max-h-[50vh] overflow-y-auto flex flex-col gap-2.5"
                style={{ background: "rgba(10,12,22,0.7)", border: "1px solid rgba(79,139,255,0.28)", backdropFilter: "blur(18px)" }}>
                {messages.map((m, i) => {
                    const isLastAssistant = m.role === "assistant" && i === messages.length - 1;
                    const speakingNow = isLastAssistant && voice.speaking && voice.enabled;
                    return (
                        <div key={i} className={`flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}>
                            <motion.div
                                className={`max-w-[88%] px-4 py-2.5 rounded-2xl text-[14.5px] leading-relaxed relative`}
                                animate={speakingNow ? {
                                    boxShadow: [
                                        "0 0 0 0 rgba(79,139,255,0.0)",
                                        "0 0 28px 6px rgba(79,139,255,0.45)",
                                        "0 0 0 0 rgba(79,139,255,0.0)",
                                    ],
                                } : { boxShadow: "0 0 0 0 rgba(0,0,0,0)" }}
                                transition={speakingNow ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
                                style={{
                                    background: m.role === "user" ? "linear-gradient(135deg, rgba(79,139,255,0.22), rgba(43,90,255,0.18))" : "rgba(255,255,255,0.05)",
                                    color: "rgba(255,255,255,0.96)",
                                    border: m.role === "user" ? "1px solid rgba(79,139,255,0.30)" : "1px solid rgba(255,255,255,0.06)",
                                    fontFamily: "Outfit, sans-serif",
                                    whiteSpace: "pre-wrap",
                                }}>
                                {m.content}
                                {speakingNow && (
                                    <span className="absolute -left-2 top-1/2 -translate-y-1/2 flex gap-0.5 pointer-events-none">
                                        <span className="w-0.5 h-3 bg-emerald-400 rounded-full" style={{ animation: "ariaVoiceBar 0.8s ease-in-out infinite" }} />
                                        <span className="w-0.5 h-4 bg-emerald-400 rounded-full" style={{ animation: "ariaVoiceBar 0.8s ease-in-out infinite 0.15s" }} />
                                        <span className="w-0.5 h-2.5 bg-emerald-400 rounded-full" style={{ animation: "ariaVoiceBar 0.8s ease-in-out infinite 0.3s" }} />
                                    </span>
                                )}
                            </motion.div>

                            {/* CTA button cliccabile sotto la risposta */}
                            {m.role === "assistant" && m.cta?.url && (
                                <a
                                    href={m.cta.url}
                                    target={m.cta.url.startsWith("http") ? "_blank" : "_self"}
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition hover:scale-[1.03]"
                                    style={{
                                        background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)",
                                        color: "white",
                                        boxShadow: "0 8px 24px -8px rgba(79,139,255,0.55)",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                    data-testid={`aria-presence-cta-${i}`}
                                >
                                    {m.cta.label}
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                                </a>
                            )}

                            {/* Chip multi-select se Aria ha proposto opzioni `▢` */}
                            {m.role === "assistant" && Array.isArray(m.options) && m.options.length >= 2 && (
                                <PresenceOptionChips
                                    options={m.options}
                                    onSubmit={(selected) => {
                                        const msg = `Mi interessano: ${selected.join(", ")}`;
                                        setInput(msg);
                                        // submit subito
                                        setTimeout(() => {
                                            document.querySelector("[data-testid='aria-presence-send']")?.click();
                                        }, 50);
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
                {sending && <div className="self-start text-white/50 text-[13px]">…</div>}
            </div>

            {/* === LEAD FORM (Phase 6.4 fix) — appare quando Aria emette [LEAD]/[LEAD:NO_MATCH]/[LEAD:NEWSLETTER] === */}
            {leadAsked && !leadSubmitted && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                    className="rounded-2xl p-4 mb-2"
                    style={{ background: "rgba(10,12,22,0.85)", border: "1px solid rgba(79,139,255,0.4)", backdropFilter: "blur(18px)" }}
                    data-testid="aria-presence-lead-form-wrap">
                    <AriaLeadForm
                        reason={leadReason}
                        sessionId={sessionId}
                        conversation={messages}
                        ctaProduct={cta?.label}
                        onClose={() => setLeadAsked(false)}
                        onSuccess={() => {
                            setLeadSubmitted(true);
                            setLeadAsked(false);
                            setMessages((m) => [...m, { role: "assistant", content: "Fatto! Ti contatto presto. Continuiamo: c'è altro che ti interessa?" }]);
                        }}
                    />
                </motion.div>
            )}

            <form onSubmit={send} className="flex items-center gap-2 rounded-2xl p-2"
                style={{ background: "rgba(10,12,22,0.78)", border: "1px solid rgba(79,139,255,0.28)", backdropFilter: "blur(18px)" }}>
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi a Aria…" disabled={sending}
                    className="flex-1 bg-transparent text-white text-[15px] placeholder-white/40 focus:outline-none px-2"
                    style={{ fontFamily: "Outfit, sans-serif" }} data-testid="aria-presence-input" autoFocus />
                <button type="submit" disabled={!input.trim() || sending}
                    className="w-10 h-10 rounded-full grid place-items-center text-white disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)" }}
                    data-testid="aria-presence-send" aria-label="Invia">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </button>
            </form>
            <button onClick={() => { voice.stop(); onClose?.(); }} className="block mx-auto mt-3 text-[12px] text-white/45 hover:text-white/75 transition"
                style={{ fontFamily: "Outfit, sans-serif" }} data-testid="aria-presence-inline-close">
                chiudi
            </button>
        </motion.div>
    );
}

/** Particle canvas + ghosted emerging features (occhi, profilo) che si formano e dissolvono.
 *  Phase 6.4 — particelle +67% (90→150) + raggio maggiore + connessioni più dense per più WOW. */
function PresenceCanvas() {
    const canvasRef = React.useRef(null);
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let W = canvas.width = window.innerWidth;
        let H = canvas.height = window.innerHeight;
        // Particle count adattivo per viewport
        const isMobile = W < 768;
        const COUNT = isMobile ? 110 : 150;
        const ctx = canvas.getContext("2d");
        const parts = Array.from({ length: COUNT }, () => ({
            x: Math.random() * W, y: Math.random() * H,
            vx: (Math.random() - 0.5) * 0.32, vy: (Math.random() - 0.5) * 0.32,
            r: Math.random() * 2.6 + 0.8,  // +44% raggio rispetto a 1.8+0.4
            a: 0.7 + Math.random() * 0.3,
        }));
        const onResize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
        window.addEventListener("resize", onResize);
        let raf;
        let t = 0;
        const loop = () => {
            t += 0.012;
            ctx.clearRect(0, 0, W, H);
            // soft connections
            for (let i = 0; i < parts.length; i++) {
                for (let j = i + 1; j < parts.length; j++) {
                    const a = parts[i], b = parts[j];
                    const dx = a.x - b.x, dy = a.y - b.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < 9000) {
                        ctx.strokeStyle = `rgba(158,195,255,${0.10 * (1 - d2 / 9000)})`;
                        ctx.lineWidth = 0.6;
                        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
                    }
                }
            }
            // particles
            for (const p of parts) {
                p.x += p.vx; p.y += p.vy;
                if (p.x < 0 || p.x > W) p.vx *= -1;
                if (p.y < 0 || p.y > H) p.vy *= -1;
                ctx.fillStyle = `rgba(158,195,255,${p.a})`;
                ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
            }
            // emerging "eyes" (accenni) — opacità modulata da sin(t)
            const eyeAlpha = Math.max(0, 0.18 + 0.16 * Math.sin(t));
            const cx = W / 2, cy = H / 2 - 60;
            ctx.fillStyle = `rgba(158,195,255,${eyeAlpha})`;
            ctx.shadowColor = `rgba(79,139,255,${eyeAlpha})`;
            ctx.shadowBlur = 20;
            ctx.beginPath(); ctx.ellipse(cx - 40, cy, 4, 1.6, 0, 0, Math.PI * 2); ctx.fill();
            ctx.beginPath(); ctx.ellipse(cx + 40, cy, 4, 1.6, 0, 0, Math.PI * 2); ctx.fill();
            ctx.shadowBlur = 0;
            raf = requestAnimationFrame(loop);
        };
        loop();
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
    }, []);
    return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ width: "100%", height: "100%" }} aria-hidden />;
}

const globalCSS = `
html, body { background: #05050F; }
::selection { background: rgba(79,139,255,0.4); color: #fff; }
`;
